import {
  CircularProgress, Grid,
} from '@mui/material';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { shadowBleed } from '../components/common/cardShadow';
import { HeaderSpacer } from '../components/Header';
import ItemCardGrid from '../components/ItemCardGrid';
import FilterBar from '../components/FilterBar';
import SelectionBar, {
  SelectionBarIconButton, SelectionBarButton, SelectionBarDivider,
  SelectionBarVerdictButton,
} from '../components/SelectionBar';
import BatchEditHeroPage from '../components/ItemHeroPage/BatchEditHeroPage';
import AddItemToListDialog from '../components/AddItemToListDialog';
import useBatchSelection from '../hooks/useBatchSelection';
import { RejectReasonMenu } from '../components/common/VerdictBar';
import {
  selectContext, selectContextError, selectContextStatus, exitSelectionMode,
} from '../redux/features/context/contextSlice';
import {
  fetchItems,
  deleteItem, batchDeleteItems, updateItem, mergeItems, shareItem, cloneItem, bookmarkItem,
} from '../redux/features/items/itemsSlice';
import { approveItem, rejectItem, batchModerateItems } from '../redux/features/pending/pendingSlice';
import StyledBackdrop from '../components/StyledBackdrop';
import ViewError from '../components/common/ViewError';
import useLoadErrorHandler from '../hooks/useLoadErrorHandler';
import Login from '../components/Login';
import { DELETE_LABEL, UNSHARE_LABEL } from '../common/ActionLabels';
import { mediaTypeName } from '../common/MediaTypes';
import {
  isPublicNamespace, mustSignIn, openItemUrlInNewTab, shareResultMessage,
} from '../common/ItemTools';
import { isItemHidden, VISIBILITY_HIDDEN, VISIBILITY_VISIBLE } from '../common/Visibility';
import { setSnackbar } from '../redux/features/snackbarSlice';
import { batchVerdictMessage, verdictPastTense, verdictResultMessage } from '../common/VerdictMessages';
import useGridLayout from '../hooks/useGridLayout';
import useFacetFilters from '../hooks/useFacetFilters';
import useConfirm from '../hooks/useConfirm';
import usePaging from '../hooks/usePaging';

function ItemsView(props) {
  const { namespace: namespaceProp, view: viewProp } = props;
  const namespace = namespaceProp || 'private';
  const { tagId } = useParams();
  const { pageSize } = useGridLayout();
  const isPublic = isPublicNamespace(namespace);

  const [searchParams] = useSearchParams();
  const page = +searchParams.get('page');
  const userFilter = searchParams.get('filter');
  const sortby = searchParams.get('sortby');
  const source = searchParams.get('source');
  const view = viewProp || searchParams.get('view');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const status = useSelector(selectContextStatus);
  const loadError = useSelector(selectContextError);
  const onLoadError = useLoadErrorHandler('items');
  const currContext = useSelector(selectContext);
  // Active facet filters live in the URL; addFilter is called when a card facet is clicked.
  const { facetParams, addFilter } = useFacetFilters();
  const { userInfo } = useSelector((state) => state.users);

  const [batchPanelOpen, setBatchPanelOpen] = useState(false);
  const [bulkAddToListOpen, setBulkAddToListOpen] = useState(false);
  const [bulkRejectAnchor, setBulkRejectAnchor] = useState(null);

  const forceSignIn = mustSignIn(namespace, page, userInfo);
  const isModerating = namespace === 'pending';
  const showDelete = Boolean(userInfo) && !isModerating;
  const showUnshare = Boolean(userInfo?.isModerator) && !isModerating;
  const isAuthenticated = !!userInfo;

  let context = null;
  if (!forceSignIn && currContext && currContext.type === 'items'
    && currContext.namespace === namespace) {
    context = currContext;
  }

  // Extract all URL parameters - single source of truth
  const urlParams = useMemo(() => ({
    page: page >= 1 ? page : 1,
    filter: userFilter,
    sortby,
    source,
    tagId,
    view,
  }), [page, userFilter, sortby, source, tagId, view]);

  // Fetch items based on current URL params
  const fetchCurrentItems = useCallback(() => {
    const pageNumber = urlParams.page;
    const offset = (pageNumber - 1) * pageSize;

    const params = {
      pageNumber,
      offset,
      limit: pageSize,
      ...facetParams,
      ...(urlParams.filter && { filter: urlParams.filter }),
      ...(urlParams.sortby && { sortby: urlParams.sortby }),
      ...(urlParams.source && { source: urlParams.source }),
      ...(urlParams.tagId && { tagId: urlParams.tagId }),
      ...(urlParams.view && { view: urlParams.view }),
    };

    dispatch(fetchItems({ namespace, params }))
      .unwrap()
      .catch(onLoadError);
  }, [namespace, urlParams, facetParams, pageSize, onLoadError]);

  // Single effect: URL changes → fetch data
  useEffect(() => {
    if (forceSignIn) return;
    fetchCurrentItems();
  }, [
    fetchCurrentItems,
    forceSignIn,
    isAuthenticated,
  ]);

  // Listen for items changes from external components to refresh view
  useEffect(() => {
    const handleCustomEvent = () => {
      if (namespace === 'private') {
        fetchCurrentItems();
      }
    };
    window.addEventListener('itemsChanged', handleCustomEvent);

    return () => {
      window.removeEventListener('itemsChanged', handleCustomEvent);
    };
  }, [namespace, fetchCurrentItems]);

  const { handleNextPage, handlePreviousPage } = usePaging(urlParams.page);

  // ----- Batch selection (private items only) -----
  const pageItems = useMemo(() => context?.items || [], [context]);
  const {
    selectionMode, selectedIds, selectedCount, selectedEntities: selectedItems,
    pageIds, selectAllLabel, selectPage, selectAll, clear: clearSelection,
  } = useBatchSelection(pageItems);
  const showSelectionUi = !isPublic && selectionMode;

  const onBatchDelete = async () => {
    if (selectedCount === 0) return;
    const ok = await confirm({
      title: 'Delete items?',
      message: `${selectedCount} item${selectedCount === 1 ? '' : 's'} will be permanently removed.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await dispatch(batchDeleteItems({ namespace, ids: selectedIds })).unwrap();
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Failed to delete some items.',
      }));
    } finally {
      dispatch(exitSelectionMode());
      fetchCurrentItems();
    }
  };
  const onBatchPanelSaved = () => {
    dispatch(exitSelectionMode());
    fetchCurrentItems();
  };

  const onItemOpen = openItemUrlInNewTab;
  const itemPath = (item) => (isModerating ? `/moderate/items/${item.id}` : `/items/${item.id}`);
  const onItemRead = (item) => navigate(itemPath(item));
  const onItemDelete = async (item) => {
    const ok = await confirm({
      title: 'Delete item?',
      message: `"${item.title || 'This item'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteItem({ namespace: 'private', itemId: item.id }))
      .unwrap()
      .then(() => {
        // Refresh current view after deleting
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to delete item.',
        }));
      });
  };
  const onItemBookmark = (item) => {
    dispatch(bookmarkItem({ itemId: item.id }))
      .unwrap()
      .then(() => {
        // Refresh current view after bookmarking
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to bookmark item.',
        }));
      });
  };
  const onItemShare = (item) => {
    dispatch(shareItem({ itemId: item.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to share the item.',
        }));
      });
  };
  const onItemVerdict = (item, kind, reason) => {
    const thunk = kind === 'approve' ? approveItem(item) : rejectItem({ id: item.id, reason });
    dispatch(thunk)
      .unwrap()
      .then(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: verdictResultMessage(kind, 'item', item.title, reason),
        }));
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to ${kind} item.`,
        }));
        fetchCurrentItems();
      });
  };

  const onBatchVerdict = async (kind, reason = null) => {
    if (selectedCount === 0) return;
    try {
      const { failed } = await dispatch(
        batchModerateItems({ ids: selectedIds, verdict: kind, reason }),
      ).unwrap();
      if (failed.length > 0) {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'warning',
          snackbarMessage: `${failed.length} of ${selectedIds.length} items could not be ${verdictPastTense(kind)}.`,
        }));
      } else {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: batchVerdictMessage(kind, 'item', selectedIds.length, reason),
        }));
      }
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: `Failed to ${kind} items.`,
      }));
    } finally {
      dispatch(exitSelectionMode());
      fetchCurrentItems();
    }
  };

  const onItemClone = (item) => {
    dispatch(cloneItem({ namespace, itemId: item.id }))
      .unwrap()
      .then(() => {
        // Refresh view after cloning to show the new item
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to clone item.',
        }));
      });
  };
  const onItemEdit = (item) => navigate(`${itemPath(item)}?edit`);
  const onItemMerge = (targetItemId, sourceItemIds) => {
    dispatch(mergeItems({ namespace, targetItemId, sourceItemIds }))
      .unwrap()
      .then(() => {
        // Refresh view after merging
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to merge items.',
        }));
      });
  };
  const onItemUnshare = async (item) => {
    const ok = await confirm({
      title: 'Unshare item?',
      message: `"${item.title || 'This item'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteItem({ namespace: 'public', itemId: item.id }))
      .unwrap()
      .then(() => {
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to unshare item.',
        }));
      });
  };
  const onItemFavorite = (item) => {
    const patch = { isFavorite: !item.isFavorite };
    dispatch(updateItem({ namespace: 'private', itemId: item.id, patch }))
      .unwrap()
      .then(() => {
        // Refresh current view after favoring
        fetchCurrentItems();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to favorite item.',
        }));
      });
  };
  const onItemHide = (item, refresh) => {
    const patch = isItemHidden(item)
      ? { visibility: VISIBILITY_VISIBLE }
      : { visibility: VISIBILITY_HIDDEN };

    dispatch(updateItem({ namespace: 'private', itemId: item.id, patch }))
      .unwrap()
      .then(() => {
        // Refresh view if requested (share view uses this)
        if (refresh) {
          fetchCurrentItems();
        }
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to update item visibility.',
        }));
      });
  };
  const onItemMarkReviewed = (item) => {
    const timeApproved = item.timeReviewed
      ? null : Math.round(Date.now() / 1000);
    const patch = { timeReviewed: timeApproved };
    dispatch(updateItem({ namespace: 'private', itemId: item.id, patch }))
    .unwrap()
    .then(() => {
      // Refresh view after marking an item as reviewed
      fetchCurrentItems();
    })
    .catch(() => {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Failed to update item as reviewed.',
      }));
    });
  };
  const onAuthorView = (id) => navigate(`/authors/${id}`);
  const onAuthorFilter = (id) => addFilter('author', id);
  const onSiteView = (id) => navigate(`/sites/${id}`);
  const onSiteFilter = (id) => addFilter('site', id);
  const onTagOpen = (id) => addFilter('tag', id);
  const onTypeView = (typeNum) => addFilter('type', mediaTypeName(typeNum));

  return (
    <>
      <HeaderSpacer />
      { !forceSignIn && <FilterBar /> }
      {confirmDialog}
      { forceSignIn && <Login />}
      { !forceSignIn && (
        <StyledBackdrop open={status === 'loading' || (!context && status !== 'failed')}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      { !forceSignIn && !context && status === 'failed' && (
        <ViewError message={loadError} entity="items" />
      )}
      { !forceSignIn && context && status !== 'loading' && (
        <Grid container sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...shadowBleed }}>
          <Grid container item sx={{ flex: 1, ...shadowBleed }}>
            <Grid xs={12} item sx={{ height: '100%' }}>
              <ItemCardGrid
                currentPage={context}
                isReadOnly={false}
                itemActions={{
                  onAuthorView,
                  onAuthorFilter,
                  onSiteView,
                  onSiteFilter,
                  onTypeView,
                  ...(isModerating
                    ? { onItemVerdict }
                    : { onItemMarkReviewed }),
                  ...(namespace === 'private' && {
                    onItemHide: (item) => onItemHide(item, view === 'share'),
                  }),
                  ...(!isModerating && { onItemClone }),
                  onItemOpen,
                  onItemRead,
                  onItemEdit,
                  ...(!isModerating && Boolean(userInfo) && { onItemFavorite }),
                  ...(showDelete && { onItemDelete }),
                  ...(showUnshare && { onItemUnshare }),
                  ...(isPublic ? { onItemBookmark } : (!isModerating && { onItemShare })),
                  onTagOpen,
                  onItemMerge,
                }}
                listActions={{
                  handlePreviousPage,
                  handleNextPage,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      { showSelectionUi && !batchPanelOpen && (
        <SelectionBar
          total={pageIds.length}
          selectAllLabel={selectAllLabel}
          onSelectPage={selectPage}
          onSelectAll={selectAll}
          onClear={clearSelection}
          actions={isModerating ? (
            <>
              <SelectionBarVerdictButton kind="approve" onClick={() => onBatchVerdict('approve')}>
                <CheckIcon sx={{ fontSize: 17 }} />
                Approve
              </SelectionBarVerdictButton>
              <SelectionBarVerdictButton
                kind="reject"
                onClick={(event) => setBulkRejectAnchor(event.currentTarget)}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
                Reject
              </SelectionBarVerdictButton>
              <RejectReasonMenu
                anchorEl={bulkRejectAnchor}
                title={`Reject ${selectedCount} as`}
                onClose={() => setBulkRejectAnchor(null)}
                onPick={(reason) => onBatchVerdict('reject', reason)}
              />
            </>
          ) : (
            <>
              <SelectionBarIconButton title="Add to list" onClick={() => setBulkAddToListOpen(true)}>
                <PlaylistAddIcon sx={{ fontSize: 24 }} />
              </SelectionBarIconButton>
              <SelectionBarIconButton
                title="Delete"
                onClick={onBatchDelete}
                hoverSx={{ background: 'rgba(244,67,54,0.25)', color: '#ff8a80' }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 24 }} />
              </SelectionBarIconButton>
              <SelectionBarDivider />
              <SelectionBarButton onClick={() => setBatchPanelOpen(true)}>
                <EditIcon sx={{ fontSize: 17 }} />
                Edit items
              </SelectionBarButton>
            </>
          )}
        />
      )}
      { batchPanelOpen && selectedItems.length > 0 && (
        <BatchEditHeroPage
          items={selectedItems}
          namespace={namespace}
          onClose={() => setBatchPanelOpen(false)}
          onSaved={onBatchPanelSaved}
        />
      )}
      <AddItemToListDialog
        open={bulkAddToListOpen}
        itemIds={selectedIds}
        onClose={() => {
          setBulkAddToListOpen(false);
          dispatch(exitSelectionMode());
        }}
      />
    </>
  );
}

export default ItemsView;
